import { useState } from "react";

import classNames from "classnames";
import { ModalComponent } from "../..";
import { useModalControl } from "../../../../customHooks/useModalControl";
import {
  formatToCurrency,
  getChannnelText,
  getOffsetFromPage,
  getPageFromOffset,
} from "../../../../helperFunctions";
import { useGetCooperativeContributionsHistoryQuery } from "../../../../redux/features/contributions/contributions.api";
import {
  CooperativeContributionsHistoryQuery,
  SingleContributionLog,
} from "../../../../redux/features/contributions/contributions.type";
import { SingleCooperativeDetails } from "../../../../redux/features/cooperative/cooperative.type";
import { SearchInput } from "../../../inputs/searchInput";
import { TableComponent } from "../../../table/table";
import { TimeText } from "../../../typography/timeText";
import { TransactionDetailsModal } from "../transactionDetailsModal";

export const CooperativeContributionsHistoryModal = ({
  isModalOpen,
  onModalClose,
  cooperativeDetails,
}: {
  isModalOpen: boolean;
  onModalClose: () => void;
  cooperativeDetails: SingleCooperativeDetails;
}) => {
  const defaultQuery: CooperativeContributionsHistoryQuery = {
    offset: 0,
    limit: 20,
    text: "",
    coop: cooperativeDetails.id,
    logs: true,
  };

  const [query, setQuery] =
    useState<CooperativeContributionsHistoryQuery>(defaultQuery);
  const [selectedEntry, setSelectedEntry] = useState<SingleContributionLog>();

  const { modal, modalOpen, handleModalOpen, handleModalClose } =
    useModalControl();

  const {
    data: cooperativeContributions,
    isFetching: loadingCooperativeContributionns,
  } = useGetCooperativeContributionsHistoryQuery(query, {
    refetchOnMountOrArgChange: true,
    skip: !isModalOpen,
  });

  return (
    <>
      <ModalComponent
        isModalOpen={isModalOpen}
        onModalClose={() => {
          onModalClose();
        }}
        title={`Cooperative Contributions History`}
        topTitle
        subTitle=""
        childrenClassName="flex flex-col"
        modalClassName="!min-h-[572px] !max-w-[1038px]"
        hasButtons={false}
      >
        <div className="flex bg-white sticky pb-6 top-0 z-10 items-center justify-between flex-wrap gap-2">
          <SearchInput
            placeholder="Search member name"
            searchFunction={(val) => {
              setQuery({ ...query, text: val ?? "", offset: 0 });
            }}
            className="w-full md:max-w-[286px] !px-4 !py-2"
            iconDimension={16}
          />
        </div>

        <div>
          <TableComponent
            className="w-full !min-w-max md:!min-w-full"
            isRowClickable={true}
            headers={[]}
            mobileCardView={false}
            onRowClick={(idx) => {
              setSelectedEntry(cooperativeContributions?.data?.results[idx]);
              handleModalOpen("transactionDetailsModal");
            }}
            isLoading={loadingCooperativeContributionns}
            isTableEmpty={[0, undefined].includes(
              cooperativeContributions?.data?.results?.length
            )}
            currentPage={getPageFromOffset(query.offset ?? 0, query.limit ?? 0)}
            isPaginated
            itemsPerPage={query?.limit}
            onPageChange={(page) => {
              setQuery({
                ...query,
                offset: getOffsetFromPage(page, query.limit ?? 0),
              });
            }}
            emptrySrc="/assets/images/empty_state_cooperatives.svg"
            emptyTitle="No Contributions Found"
            totalCount={cooperativeContributions?.data?.count}
            rowClassName=""
            rows={
              cooperativeContributions?.data?.results?.map((item, idx) => {
                const key = `$contributions-history-${idx}`;

                return {
                  id: item?.id,
                  items: [
                    <div
                      key={`${key}-details`}
                      className="flex items-center gap-3 overflow-hidden"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-bold">
                          {`${item?.MembershipWalletID?.MemberID?.User?.FirstName} ${item?.MembershipWalletID?.MemberID?.User?.LastName}`}
                        </p>

                        <div className="flex items-center gap-1 text-xs text-pry-dark-green-400/60">
                          {getChannnelText(item?.inAppPayment)}
                          <p>∙</p>
                          <TimeText
                            timeString={item?.PaymentDateAdded}
                            showTime={false}
                          />
                        </div>
                      </div>

                      <p
                        className={classNames("font-bold text-sm", {
                          "text-success-600": item?.flow_type === "in",
                          "text-error-600": item?.flow_type === "out",
                        })}
                      >
                        {item?.flow_type === "in" ? "+" : "-"}
                        {formatToCurrency(item.Amount, {
                          decimalPlaces: 2,
                          currency: "NGN",
                        })}
                      </p>
                    </div>,
                  ],
                };
              }) ?? []
            }
          />
        </div>
      </ModalComponent>

      {selectedEntry ? (
        <TransactionDetailsModal
          isModalOpen={modalOpen && modal === "transactionDetailsModal"}
          onModalClose={() => {
            setSelectedEntry(undefined);
            handleModalClose();
          }}
          transaction={selectedEntry}
          type={"contribution"}
        />
      ) : undefined}
    </>
  );
};
